/**
 * GET `/roster/issued_roster` — 이 훅의 응답 타입은 `@vTypes/snapshot-type`의 {@link IssuedRosterSnapshotResponse} 와 동일.
 *
 * - 성공 **200**: 서비스 dict를 그대로 JSON (별도 response_model 없음). `get_issued_roster_snapshot_service` 반환값.
 * - `meta` / `config`: DB `*_json`. `nurses`: nurses_json, `roster`: year·month·days_in_month·shift_colors·nurses(schedule: `{ code, color }[]`, schedule_ids, counts) 등.
 * - 오류: **401** 미인증 · **404** Issued snapshot not found · **500** 기타.
 * - 쿼리: `year`, `month` 필수. **마스터 관리자만** `group_id` 지정, 그 외는 세션 group_id.
 */

import useStore from "@store/store";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { IssuedRosterSnapshotResponse } from "@vTypes/snapshot-type";

export type { IssuedRosterSnapshotResponse };

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function stringField(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === "string" ? value : null;
}

function resignationEntries(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (entry) => isRecord(entry) && stringField(entry, "reason") === "퇴사"
  );
}

export default function useSnapShot({
  year,
  month,
  /** 비우면 세션 `loginUser.group_id` — URL 등에서 넘긴 병동으로 스냅샷 조회 */
  groupId,
  enabled = true,
}: {
  year?: number | string;
  month?: number | string;
  groupId?: string | null;
  enabled?: boolean;
} = {}) {
  const { loginUser } = useStore();
  const normalizedYear = year ? Number(year) : undefined;
  const normalizedMonth = month ? Number(month) : undefined;
  const effectiveGroupId = (() => {
    const fromProp = (groupId ?? "").trim();
    if (fromProp !== "") return fromProp;
    return (loginUser?.group_id ?? "").trim();
  })();

  return useQuery<IssuedRosterSnapshotResponse | null>({
    queryKey: [...QUERY_KEYS.roster.issuedRosterSnapshot(
      normalizedYear as number,
      normalizedMonth as number,
      effectiveGroupId
    ), loginUser?.account_id, loginUser?.office_id],
    queryFn: async ({ signal }) => {
      const y = normalizedYear as number;
      const m = normalizedMonth as number;
      try {
        const result = await httpClient.get<IssuedRosterSnapshotResponse>(
          "/roster/issued_roster",
          {
            params: {
              year: y,
              month: m,
              group_id: effectiveGroupId,
            },
            signal,
          }
        );
        if (import.meta.env.DEV && result) {
          const rootAssignments = result.assignments ?? {};
          const resignationAssignments = Object.entries(rootAssignments)
            .map(([nurseId, entries]) => ({
              nurseId,
              entries: entries.filter((entry) => entry.reason === "퇴사"),
            }))
            .filter((item) => item.entries.length > 0);
          const rosterRows: unknown[] = result.roster?.nurses ?? [];
          const rowAssignments = rosterRows
            .filter(isRecord)
            .map((nurse) => {
              const nurseId =
                stringField(nurse, "nurse_id") ?? stringField(nurse, "id") ?? "";
              return {
                nurseId,
                name: stringField(nurse, "name"),
                assignments: resignationEntries(nurse["assignments"]),
                inbound: resignationEntries(nurse["inbound"]),
              };
            })
            .filter(
              (row) => row.assignments.length > 0 || row.inbound.length > 0
            );
          const profileRows: unknown[] = result.nurses ?? [];
          const profileResignations = profileRows
            .filter(isRecord)
            .filter((nurse) => stringField(nurse, "resignation_date") != null)
            .map((nurse) => {
              return {
                nurseId: stringField(nurse, "nurse_id"),
                name: stringField(nurse, "name"),
                resignationDate: stringField(nurse, "resignation_date"),
                resignationReason: stringField(nurse, "resignation_reason"),
              };
            });
          console.info("[resignation-debug] GET /roster/issued_roster", {
            year: y,
            month: m,
            groupId: effectiveGroupId,
            profileResignations,
            resignationAssignments,
            rowAssignments,
          });
        }
        return result;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled:
      enabled &&
      !!loginUser &&
      !!normalizedYear &&
      !!normalizedMonth &&
      effectiveGroupId !== "",
    staleTime: Infinity,
  });
}
